// src/components/operationCenter/widgets/thesis/ThesisHealthGauge.tsx
// Health score visualization for thesis conditions

import React from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import type { ThesisHealthTrend } from "@/types/thesis";

interface ThesisHealthGaugeProps {
  score: number;
  trend: ThesisHealthTrend;
  conditionsMet: number;
  conditionsTotal: number;
  compact?: boolean;
}

const TREND_CONFIG: Record<
  ThesisHealthTrend,
  { icon: React.ElementType; label: string; className: string }
> = {
  improving: {
    icon: TrendingUp,
    label: "Improving",
    className: "text-emerald-400",
  },
  stable: {
    icon: Minus,
    label: "Stable",
    className: "text-slate-400",
  },
  deteriorating: {
    icon: TrendingDown,
    label: "Deteriorating",
    className: "text-red-400",
  },
};

function getScoreColor(score: number): string {
  if (score >= 80) return "text-emerald-400";
  if (score >= 60) return "text-lime-400";
  if (score >= 40) return "text-amber-400";
  if (score >= 20) return "text-orange-400";
  return "text-red-400";
}

function getGaugeGradient(score: number): string {
  if (score >= 80) return "from-emerald-500 to-emerald-400";
  if (score >= 60) return "from-lime-500 to-lime-400";
  if (score >= 40) return "from-amber-500 to-amber-400";
  if (score >= 20) return "from-orange-500 to-orange-400";
  return "from-red-500 to-red-400";
}

export const ThesisHealthGauge: React.FC<ThesisHealthGaugeProps> = ({
  score,
  trend,
  conditionsMet,
  conditionsTotal,
  compact = false,
}) => {
  const trendConfig = TREND_CONFIG[trend];
  const TrendIcon = trendConfig.icon;
  const scoreColor = getScoreColor(score);
  const gaugeGradient = getGaugeGradient(score);

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        {/* Mini circular gauge */}
        <div className="relative w-10 h-10">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
            {/* Background circle */}
            <circle
              cx="18"
              cy="18"
              r="15"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              className="text-slate-700"
            />
            {/* Progress circle */}
            <circle
              cx="18"
              cy="18"
              r="15"
              fill="none"
              stroke="url(#gaugeGradient)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray={`${(score / 100) * 94.2} 94.2`}
            />
            <defs>
              <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" className={scoreColor} stopColor="currentColor" />
                <stop offset="100%" className={scoreColor} stopColor="currentColor" />
              </linearGradient>
            </defs>
          </svg>
          <span className={`absolute inset-0 flex items-center justify-center text-xs font-bold ${scoreColor}`}>
            {score}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-[10px] text-slate-500">
            {conditionsMet}/{conditionsTotal}
          </span>
          <span className={`text-[9px] flex items-center gap-0.5 ${trendConfig.className}`}>
            <TrendIcon className="w-2.5 h-2.5" />
            {trendConfig.label}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center">
      {/* Circular gauge */}
      <div className="relative w-20 h-20">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
          {/* Background circle */}
          <circle
            cx="18"
            cy="18"
            r="15"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            className="text-slate-700"
          />
          {/* Progress circle */}
          <circle
            cx="18"
            cy="18"
            r="15"
            fill="none"
            stroke="url(#gaugeGradientFull)"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={`${(score / 100) * 94.2} 94.2`}
            className="transition-all duration-500 ease-out"
          />
          <defs>
            <linearGradient id="gaugeGradientFull" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" className={scoreColor} stopColor="currentColor" />
              <stop offset="100%" className={scoreColor} stopColor="currentColor" />
            </linearGradient>
          </defs>
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-xl font-bold ${scoreColor}`}>{score}</span>
          <span className="text-[8px] text-slate-500 uppercase tracking-wide">Health</span>
        </div>
      </div>

      {/* Bar underneath */}
      <div className="w-full mt-2">
        <div className="h-1.5 rounded-full bg-slate-700 overflow-hidden">
          <div
            className={`h-full rounded-full bg-gradient-to-r ${gaugeGradient} transition-all duration-500 ease-out`}
            style={{ width: `${score}%` }}
          />
        </div>
      </div>

      {/* Stats row */}
      <div className="flex items-center justify-between w-full mt-2 px-1">
        <span className="text-[10px] text-slate-400">
          {conditionsMet}/{conditionsTotal} conditions met
        </span>
        <span className={`text-[10px] flex items-center gap-0.5 ${trendConfig.className}`}>
          <TrendIcon className="w-3 h-3" />
          {trendConfig.label}
        </span>
      </div>
    </div>
  );
};

export default ThesisHealthGauge;
